// Shared filter helper — news rows respect the same Intel FilterState shape
// the SSE feed uses, so a filter-rail change reflows the news side panel
// (W2-C) the same way it reflows the map.
//
// Supported facets (Wave 2):
//   * severity   { min, max }
//   * time_window { from, to } against `published_at`
//   * region      multi-enum of 2-letter region codes
//   * source      multi-enum of source.name
//
// Other Intel facets (signal_type, linked_to_opportunity, aoi) don't apply
// here — news rows have no signal_type, and AOI matching belongs to the
// signal layer. The signal-level filter still gates which signals exist,
// which gates which news rows light up via `linked_signal_ids`.

import type { filters as f } from '@eidollon/contracts';
import type { NewsItem } from './types.js';

export function applyNewsFilter(rows: readonly NewsItem[], state: f.FilterState): NewsItem[] {
  // biome-ignore lint/complexity/useLiteralKeys: FilterState is a Zod record (index signature) and tsconfig's noPropertyAccessFromIndexSignature requires bracket access.
  const sev = state['severity'] as { min?: number | null; max?: number | null } | undefined;
  // biome-ignore lint/complexity/useLiteralKeys: index signature.
  const time = state['time_window'] as { from?: string | null; to?: string | null } | undefined;
  // biome-ignore lint/complexity/useLiteralKeys: index signature.
  const regions = state['region'];
  // biome-ignore lint/complexity/useLiteralKeys: index signature.
  const sources = state['source'];

  const regionSet = Array.isArray(regions)
    ? new Set(
        (regions as readonly unknown[])
          .filter((x): x is string => typeof x === 'string')
          .map((s) => s.toUpperCase()),
      )
    : null;
  const sourceSet = Array.isArray(sources)
    ? new Set((sources as readonly unknown[]).filter((x): x is string => typeof x === 'string'))
    : null;

  return rows.filter((r) => {
    if (sev) {
      if (sev.min != null && r.severity < sev.min) return false;
      if (sev.max != null && r.severity > sev.max) return false;
    }
    if (time) {
      if (time.from && r.published_at < time.from) return false;
      if (time.to && r.published_at > time.to) return false;
    }
    if (regionSet && regionSet.size > 0 && !regionSet.has(r.region.toUpperCase())) return false;
    if (sourceSet && sourceSet.size > 0) {
      // Source filter from the Intel facet keys off SOURCE codes (OFAC, EU, …)
      // not outlet names. We match either field so the Inference orchestrator
      // can choose its taxonomy later.
      const hit = sourceSet.has(r.source.name) || sourceSet.has(r.source.name.toUpperCase());
      if (!hit) return false;
    }
    return true;
  });
}
